import { ApiError, AppError, Language } from '@/types/types';
import { Locale, formatDistance } from 'date-fns';
import { flatten } from 'lodash';
import { isAxiosError } from 'axios';
import { DateValue } from '@mantine/dates';
import { ErrorCode } from '@/configs/errorCode';
import {
	CreatorState,
	PlaygroundShapeType,
	SerializedProjectConfiguration,
} from '@/types/creator';
import { SerializedRenderer } from '@/renderer/types';
import { ProductModule } from '@/api/ProductModuleApi';
import { CreatorProductModule } from '@/api/CreatorProductModuleApi';
import { CreatorLandscapeModule } from '@/api/CreatorLandscapeModuleApi';
import { LandscapeModule } from '@/api/LandscapeModuleApi';
import APP_CONFIG from '@/configs/appConfig';
import moment from 'moment';

const PHONE_REGEX =
	/^\s*(?:\+?(\d{1,3}))?([-. (]*(\d{3})[-. )]*)?((\d{3})[-. ]*(\d{2,4})(?:[-.x ]*(\d+))?)\s*$/;

/**
 *
 * @param phoneNumber validate international
 * @returns
 */
export function validatePhoneNumber(phoneNumber: string) {
	return PHONE_REGEX.test(phoneNumber) && phoneNumber.length >= 9;
}

export function formatDateForPresentation(date: moment.MomentInput) {
	if (!date) return '-';
	return moment(date).format('DD.MM.YYYY');
}

export function formatTimeForPresentation(
	date: moment.MomentInput | string = new Date()
) {
	return moment(date).format('HH:mm');
}

export function formatExactDate(date: moment.MomentInput = new Date()) {
	return `${formatDateForPresentation(date)}, ${formatTimeForPresentation(
		date
	)}`;
}

export function sortByDate(
	dateA: string | Date,
	dateB: string | Date,
	order: 'asc' | 'desc' = 'asc'
) {
	const first = moment(dateA).valueOf(),
		second = moment(dateB).valueOf();

	return order === 'asc' ? first - second : second - first;
}

export function stripHTML(string: string) {
	return string.replace(/(<([^>]+)>)/gi, '');
}

export const ID_REGEX =
	/[a-f0-9]{8}-?[a-f0-9]{4}-?[a-f0-9]{4}-?[89ab][a-f0-9]{3}-?[a-f0-9]{12}/g;

export function getIdsFromText(text: string) {
	return [...new Set(flatten([...text.matchAll(ID_REGEX)]))];
}

export function fileTypeToHeader(fileType: 'csv' | 'xlsx' | 'pdf') {
	switch (fileType) {
		case 'xlsx':
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
		case 'csv':
			return 'text/csv';
		case 'pdf':
			return 'application/pdf';
		default:
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
	}
}

export function getContent(conent: Record<string, string>, slug: string) {
	return conent[slug];
}

export function formatDateFromNow(date: Date, locale: Locale) {
	return formatDistance(date, new Date(), {
		locale,
		includeSeconds: true,
		addSuffix: true,
	});
}

export const formatPrice = (value: number, opts?: Intl.NumberFormatOptions) => {
	const formatter = new Intl.NumberFormat('pl', {
		style: 'currency',
		currency: 'PLN',
		currencyDisplay: 'code',
		maximumFractionDigits: !value ? 0 : undefined,
		...opts,
	});

	return formatter.format(value);
};

export const isApiError = (error: unknown): error is ApiError => {
	return (
		!!(error as AppError).status &&
		!!(error as AppError).stack &&
		!!(error as ApiError).message
	);
};

export const isAppError = (error: unknown): error is AppError => {
	return !!(error as AppError).status && !!(error as AppError).stack;
};

export const getErrorCodes = (error: unknown) => {
	if (isAxiosError<ApiError>(error) && error.response)
		return Array.isArray(error.response.data.message)
			? error.response.data.message
			: [error.response.data.message];

	return [ErrorCode.GENERIC];
};

export const errorCodesToMessage = (errorCodes: ErrorCode[]) =>
	errorCodes.map((code) => `errors.${code}`).join(', ');

export function getFileSrc(path?: string) {
	return `${import.meta.env.VITE_API_URL_UPLOADS}${path}`;
}

export function formatDateForApi(date: DateValue) {
	if (!date) return null;

	return moment(date).format('YYYY-MM-DD');
}

export function isDefaultLanguage(language: Language): language is 'PL' {
	return language === APP_CONFIG.DEFAULT_LANGUAGE;
}

export function natSort(nameA = '', nameB = '', order: 'asc' | 'desc' = 'asc') {
	let a: string | number = Number(nameA),
		b: string | number = Number(nameB);

	if (isNaN(a) || isNaN(b)) {
		return order === 'asc'
			? nameA.localeCompare(nameB)
			: nameB.localeCompare(nameA);
	}

	return order === 'asc' ? a - b : b - a;
}

export function inputWidthValue(value: string) {
	return `${value.length + 3}ch`;
}

export function roundToHalf(num: number) {
	return Math.round(num * 2) / 2;
}

export function mapShapeTypeName(shape: PlaygroundShapeType) {
	switch (shape) {
		case PlaygroundShapeType.RECTANGLE:
			return 'Prostokąt';
		case PlaygroundShapeType.CIRCLE:
			return 'Okrąg';
		case PlaygroundShapeType.CUSTOM:
			return 'Własny kształt';
		default:
			throw new Error('INVALID_SHAPE_TYPE');
	}
}

export function isCreatorActionsEnabled({ options }: CreatorState) {
	return !options.isDrawing && !!options.selectedShape;
}

export function generateProjectConfigurationPayload(
	{
		options,
		creatorProductModules,
		creatorLandscapeModules,
		renderer: rendererSlice,
	}: CreatorState,
	rendererState: SerializedRenderer
): SerializedProjectConfiguration {
	return {
		creatorProductModules: creatorProductModules.map((creaProdMod) => ({
			refId: creaProdMod.refId,
			productModule: { id: creaProdMod.productModule.id },
		})),
		creatorLandscapeModules: creatorLandscapeModules.map((creaLandMod) => ({
			refId: creaLandMod.refId,
			landscapeModule: { id: creaLandMod.landscapeModule.id },
		})),
		options: {
			state: {
				renderer: rendererSlice,
				options,
			},
			renderer: rendererState,
		},
		isActive: true,
	};
}

export function generateProjectPayload(
	state: CreatorState,
	rendererState: SerializedRenderer
) {
	return structuredClone({
		name: state.name,
		configuration: generateProjectConfigurationPayload(state, rendererState),
		contact: state.contact,
	});
}

export function saveProjectToLocal(
	state: CreatorState,
	rendererState: SerializedRenderer
) {
	localStorage.setItem(
		APP_CONFIG.AUTO_SAVE.KEY,
		JSON.stringify(generateProjectPayload(state, rendererState))
	);
}

export function getProxyImage(src?: string) {
	return `${import.meta.env.VITE_API_URL}proxy/image?url=${src}`;
}

export function getNewCreatorProductModule(productModule: ProductModule) {
	const creatorProductModule: CreatorProductModule = {
		id: crypto.randomUUID(),
		refId: crypto.randomUUID(),
		productModule,
		options: {},
		createdAt: new Date().toISOString(),
		updatedAt: new Date().toISOString(),
	};

	return creatorProductModule;
}

export function getNewCreatorLandscapeModule(landscapeModule: LandscapeModule) {
	const creatorLandscapeModule: CreatorLandscapeModule = {
		id: crypto.randomUUID(),
		refId: crypto.randomUUID(),
		landscapeModule,
		options: {},
		createdAt: new Date().toISOString(),
		updatedAt: new Date().toISOString(),
	};

	return creatorLandscapeModule;
}
